import { ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useGroupStore } from './group';
import { useOnDemandStore } from './on-demand';
import api from '@/api';
import type { DeviceCount, OnlineDevice } from '@/@types/device';
import type { Vehicle } from '@/@types/vehicle';
import { isMockTagsEnabled, mockOnlineDevices, mockVehicles } from '@/libs/dev/mock-tags';
import { isAppVisible } from '@/libs/app-visibility';
import { createHeadingTracker } from '@/libs/helpers/vehicle-heading';
import { countOnDemandVehicles } from '@/libs/on-demand-helper';

export interface DeviceFilter {
  routeIds?: string[];
  onDemandRouteIds?: string[];
  deviceId?: string;
  tripId?: string;
}

export const useDevicesStore = defineStore('devices', () => {
  const DEVICES_COUNT_REFRESH_INTERVAL = 20000;
  const ONLINE_DEVICES_REFRESH_INTERVAL = 5000;

  const deviceCountTimeOutRef = ref<number | undefined>();
  const deviceTimeOutRef = ref<number | undefined>();
  const groupStore = useGroupStore();
  const onDemandStore = useOnDemandStore();

  const onlineDevicesCount = ref<Array<DeviceCount>>([]);
  const onlineDevices = ref<Array<OnlineDevice>>([]);
  const vehicles = ref<Array<Vehicle>>([]);
  /** Vehicles serving each on-demand route, refreshed with the counts of the GTFS routes. */
  const onDemandVehicleCounts = ref<Map<string, number>>(new Map());
  // When the current vehicle polling last got an answer, and when it (re)started:
  // together they tell a lost connection from a poll that has not answered yet.
  const onlineDevicesAnsweredAt = ref<number | undefined>();
  const onlineDevicesPollingSince = ref<number>(Date.now());
  const headingTracker = createHeadingTracker();

  // Bumped on every scope change so a response for a superseded scope, still
  // in flight, cannot overwrite the current counts.
  let countRun = 0;
  // Same guard for the vehicle list: its filters change on every navigation
  // between the home, stop and trip views.
  let devicesRun = 0;

  async function getOnlineDevicesCount(routeIds: string[], run: number) {
    const groupId = groupStore.group?.group_id;
    if (!groupId) return;
    let answer: DeviceCount[];
    try {
      answer = await api.getOnlineDevicesCount(groupId, routeIds);
    } catch {
      // The last counts stay on screen until the next poll
      return;
    }
    if (run === countRun) onlineDevicesCount.value = answer;
  }

  // The count endpoint knows no on-demand route: their vehicles are counted from the list.
  async function getOnDemandVehicleCounts(run: number) {
    const groupId = groupStore.group?.group_id;
    const onDemandRouteIds = onDemandStore.routeIds;
    if (!groupId || onDemandRouteIds.length === 0) return;
    let answer: OnlineDevice[];
    try {
      answer = await api.getOnlineDevices(groupId, { onDemandRouteIds });
    } catch {
      return;
    }
    if (run === countRun) onDemandVehicleCounts.value = countOnDemandVehicles(answer);
  }

  async function getOnlineDevices(filters: DeviceFilter, run: number) {
    const groupId = groupStore.group?.group_id;
    if (!groupId) return;
    let answer: OnlineDevice[];
    try {
      answer = await api.getOnlineDevices(groupId, filters);
    } catch {
      return;
    }
    if (run !== devicesRun) return;
    onlineDevicesAnsweredAt.value = Date.now();
    answer.forEach(device => {
      if (device.on_demand_route_id) {
        device.bearing = device.latlng ? headingTracker.update(device.device_id, device.latlng) : undefined;
      }
    });
    headingTracker.prune(new Set(answer.map(device => device.device_id)));
    onlineDevices.value =
      import.meta.env.DEV && isMockTagsEnabled() ? [...answer, ...mockOnlineDevices(filters)] : answer;
  }

  async function loadVehicles() {
    const groupId = groupStore.group?.group_id;
    if (!groupId) return;
    let answer: Vehicle[];
    try {
      answer = await api.getVehicles(groupId);
    } catch {
      return;
    }
    vehicles.value = import.meta.env.DEV && isMockTagsEnabled() ? [...answer, ...mockVehicles()] : answer;
  }

  // Consumers of the count polling, keyed per component instance with the
  // route scope each one needs. Reference counting is required because
  // kept-alive trees overlap during a route transition (the entering tree
  // mounts before the leaving one deactivates): a plain stop from the leaving
  // consumer would kill the interval the entering one just armed.
  const deviceCountConsumers = new Map<symbol, string[]>();

  function mergedCountRouteIds(): string[] {
    const scopes = [...deviceCountConsumers.values()];
    // An empty scope means "all routes" and supersedes any filter.
    if (scopes.some(scope => scope.length === 0)) return [];
    return [...new Set(scopes.flat())];
  }

  function restartDevicesCountPolling() {
    window.clearInterval(deviceCountTimeOutRef.value);
    deviceCountTimeOutRef.value = undefined;
    const routeIds = mergedCountRouteIds();
    const run = ++countRun;
    if (!isAppVisible.value) return;
    // A scope narrowed to GTFS routes (a route view) has no use for the on-demand counts.
    const poll = () => {
      getOnlineDevicesCount(routeIds, run);
      if (routeIds.length === 0) getOnDemandVehicleCounts(run);
    };
    poll();
    deviceCountTimeOutRef.value = window.setInterval(poll, DEVICES_COUNT_REFRESH_INTERVAL);
  }

  function acquireDevicesCount(consumer: symbol, routeIds: string[] = []) {
    // Re-acquiring is a no-op: a kept-alive component fires both mounted and
    // activated on its initial mount.
    if (deviceCountConsumers.has(consumer)) return;
    deviceCountConsumers.set(consumer, routeIds);
    restartDevicesCountPolling();
  }

  function releaseDevicesCount(consumer: symbol) {
    if (!deviceCountConsumers.delete(consumer)) return;
    if (deviceCountConsumers.size > 0) {
      restartDevicesCountPolling();
      return;
    }
    countRun++;
    window.clearInterval(deviceCountTimeOutRef.value);
    deviceCountTimeOutRef.value = undefined;
    onlineDevicesCount.value = [];
    onDemandVehicleCounts.value = new Map();
  }

  // Kept while the app is hidden, so the poll resumes on the same vehicles.
  let devicesFilters: DeviceFilter | undefined;

  function restartDevicesPolling() {
    window.clearInterval(deviceTimeOutRef.value);
    deviceTimeOutRef.value = undefined;
    const run = ++devicesRun;
    const filters = devicesFilters;
    onlineDevicesPollingSince.value = Date.now();
    if (!filters || !isAppVisible.value) return;
    getOnlineDevices(filters, run);
    deviceTimeOutRef.value = window.setInterval(() => {
      getOnlineDevices(filters, run);
    }, ONLINE_DEVICES_REFRESH_INTERVAL);
  }

  function startGetDevices(filters: DeviceFilter = {}) {
    devicesFilters = filters;
    onlineDevicesAnsweredAt.value = undefined;
    restartDevicesPolling();
  }

  function stopGetDevices() {
    devicesFilters = undefined;
    restartDevicesPolling();
    onlineDevices.value = [];
    onlineDevicesAnsweredAt.value = undefined;
  }

  // Hidden, the app stops polling; visible again, each active poll refreshes at once.
  watch(isAppVisible, () => {
    restartDevicesPolling();
    if (deviceCountConsumers.size > 0) restartDevicesCountPolling();
  });

  // The on-demand routes load with the network, often after the counts started.
  watch(
    () => onDemandStore.routeIds.join(','),
    () => {
      if (deviceCountConsumers.size > 0) restartDevicesCountPolling();
    },
  );

  return {
    onlineDevicesCount,
    onlineDevices,
    onlineDevicesAnsweredAt,
    onlineDevicesPollingSince,
    onDemandVehicleCounts,
    vehicles,
    acquireDevicesCount,
    releaseDevicesCount,
    startGetDevices,
    stopGetDevices,
    loadVehicles,
  };
});
